/* eslint-disable react/no-unescaped-entities -- legacy card copy quotes dynamic values as "{value}" throughout this file */
import {
  Icon,
  Flex,
  Text,
  Center,
  Link,
  Button,
  Avatar,
} from '@chakra-ui/react';
import { decode } from 'js-base64';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationTime } from '../../components/Time';
import { InterviewersInfo } from '@/components/shared/interviews/components';
import { LinkExternal01 } from '@kula-ai/icons-core';
import { FaCircleMinus } from 'react-icons/fa6';
import { FaCheckCircle, FaCircle } from 'react-icons/fa';

export const MeetingContainer = ({
  notification = {},
  onClose = () => {},
  type,
  icon,
  iconColor,
  iconbg,
  showButton = false,
}) => {
  const [markAsRead] = useMarkAsReadMutation();
  const { custom_attributes } = notification;
  const data = JSON.parse(decode(custom_attributes));

  const {
    interview_date,
    interview_name,
    job_name,
    interview_time,
    candidate,
    interviewers,
    interview_location_type,
    interview_location,
    event_url,
    interview_url,
    attendee,
    attendee_is_candidate = false,
    rsvp_comment,
    rsvp_status = 'Tentative',
    meeting_rooms = [],
    requested_by = {},
  } = data || {};

  const isReminderNotificationType = [
    'rsvpReminder',
    'rsvpReminderNotification',
  ].includes(type);
  const renderCancelText = () => (
    <Flex flexWrap="wrap" gap="2px">
      <Text size="sm-semi-bold">"{interview_name}"</Text>
      <Text fontSize="sm">interview for job</Text>
      <Text size="sm-semi-bold">"{job_name}"</Text>
      <Text fontSize="sm">has been cancelled</Text>
    </Flex>
  );

  const renderBookedText = () => (
    <Flex flexWrap="wrap" gap="2px">
      <Text size="sm-semi-bold">"{interview_name}"</Text>
      <Text fontSize="sm">interview with</Text>
      <Text size="sm-semi-bold">"{candidate?.name}"</Text>
      <Text fontSize="sm">has been booked for job</Text>
      <Text size="sm-semi-bold">"{job_name}"</Text>
    </Flex>
  );

  const renderCandidateBookedText = () => (
    <Flex flexWrap="wrap" gap="2px">
      <Text size="sm-semi-bold">"{candidate?.name}"</Text>
      <Text fontSize="sm"> has booked a meeting slot for the</Text>
      <Text size="sm-semi-bold">"{interview_name}"</Text>
      <Text fontSize="sm">interview for job</Text>
      <Text size="sm-semi-bold">"{job_name}"</Text>
    </Flex>
  );

  const renderStartingSoonText = () => (
    <Flex flexWrap="wrap" gap="2px">
      <Text size="sm-semi-bold">"{interview_name}"</Text>
      <Text fontSize="sm">interview for job</Text>
      <Text size="sm-semi-bold">"{job_name}"</Text>
      <Text fontSize="sm">is starting soon! </Text>
    </Flex>
  );

  const rescheduleText = () => (
    <Flex flexWrap="wrap" gap="2px">
      <Text size="sm-semi-bold">"{interview_name}"</Text>
      <Text fontSize="sm">interview has been rescheduled for job</Text>
      <Text size="sm-semi-bold">"{job_name}"</Text>
    </Flex>
  );

  const rsvpReminderText = () => (
    <Flex flexWrap="wrap" gap="2px">
      <Text size="sm-semi-bold">Reminder:</Text>
      <Text fontSize="sm">you haven’t RSVP’d your invitation for the </Text>
      <Text size="sm-semi-bold">"{interview_name}"</Text>
      <Text fontSize="sm">meeting. Please do so now.</Text>
    </Flex>
  );

  // Match the recruiter RSVP copy rendered by the email/slack channels: the interviewer variant
  // reads "<interviewer> has <verb> <interview> with <candidate>", while a candidate RSVP drops the
  // "with <candidate>" clause. attendee_is_candidate is sent in custom_attributes by core.
  const rsvpVerb = {
    Confirmed: 'accepted',
    Declined: 'declined',
    Tentative: 'tentatively accepted',
  };

  const rsvpReminderNotificationText = () => (
    <Flex flexWrap="wrap" gap="2px">
      <Text size="sm-semi-bold">"{attendee?.name}"</Text>
      <Text fontSize="sm">has {rsvpVerb[rsvp_status] || 'responded to'}</Text>
      <Text size="sm-semi-bold">"{interview_name}"</Text>
      {!attendee_is_candidate && (
        <>
          <Text fontSize="sm">with</Text>
          <Text size="sm-semi-bold">"{candidate?.name}"</Text>
        </>
      )}
    </Flex>
  );

  const type_text = {
    cancelled: renderCancelText,
    booked: renderBookedText,
    candidateBooked: renderCandidateBookedText,
    startingSoon: renderStartingSoonText,
    reschedule: rescheduleText,
    rsvpReminder: rsvpReminderText,
    rsvpReminderNotification: rsvpReminderNotificationText,
  };

  const meetingStatus = {
    Confirmed: {
      id: 'accepted',
      icon: FaCheckCircle,
      color: '#5FBB3F',
      label: 'Confirmed',
    },
    Declined: {
      id: 'declined',
      icon: FaCircleMinus,
      color: '#F05858',
      label: 'Declined',
    },
    Tentative: {
      id: 'tentative',
      icon: FaCircle,
      color: '#FFBE41',
      label: 'Tentative',
    },
  };

  return (
    <Flex alignItems="baseline" gap="6px">
      <Center minW="46px" h="46px" borderRadius="full" bg={iconbg}>
        <Icon as={icon} color={iconColor} />
      </Center>
      <Flex flexDir="column" ml="16px" gap="6px" alignSelf="start">
        {type_text[type] && type_text[type]()}

        <Text size="sm-semi-bold" mt="8px">
          Details
        </Text>

        <Flex
          border="1px solid"
          borderColor="border-color"
          borderRadius="sm"
          p="14px"
          gap="12px"
          flexDir="column"
        >
          <Flex flexDirection="column" gap="2px">
            <Text size="sm-semi-bold">Date</Text>
            <Text fontSize="sm">{interview_date}</Text>
          </Flex>
          <Flex flexDirection="column" gap="2px">
            <Text size="sm-semi-bold">Time</Text>
            <Text fontSize="sm">{interview_time}</Text>
          </Flex>

          {type === 'rsvpReminderNotification' && (
            <Flex flexDirection="column" gap="2px">
              <Text size="sm-semi-bold">RSVP</Text>
              <Flex gap="5px">
                <Flex gap="3px">
                  <Avatar
                    name={attendee?.name}
                    src={attendee?.avatar}
                    borderRadius={'4px'}
                    size={'2xs'}
                  />
                  <Text fontSize="sm">{attendee?.name}</Text>
                </Flex>
                <Flex gap="3px" alignItems="center">
                  <Icon
                    as={meetingStatus[rsvp_status]?.icon}
                    w="13px"
                    h="13px"
                    color={meetingStatus[rsvp_status]?.color}
                  />
                  <Text fontSize="sm">{meetingStatus[rsvp_status]?.label}</Text>
                </Flex>
              </Flex>
            </Flex>
          )}

          {rsvp_comment && (
            <Flex flexDirection="column" gap="2px">
              <Text size="sm-semi-bold">Note</Text>
              <Text fontSize="sm">{rsvp_comment}</Text>
            </Flex>
          )}
          {interview_location_type && (
            <Flex flexDirection="column" gap="2px">
              <Text size="sm-semi-bold">Where</Text>
              {!(
                interview_location_type === 'Google Meet' ||
                interview_location_type === 'Zoom'
              ) ? (
                <Text fontSize="sm">{interview_location_type}</Text>
              ) : (
                <Link
                  fontSize="12px"
                  fontWeight="semiBold"
                  display="flex"
                  alignItems="center"
                  gap="3px"
                  href={interview_location}
                  target="_blank"
                  data-test-id="interview-location-link"
                >
                  <Text fontSize="sm">{interview_location_type}</Text>
                  <LinkExternal01 size={12} className="kula-icon" />
                </Link>
              )}
            </Flex>
          )}
          <Flex flexDirection="column" gap="2px">
            <Text size="sm-semi-bold">Guests</Text>
            <InterviewersInfo
              users={[candidate, ...interviewers]}
              isHeader
              size="2xs"
              borderRadius="4px"
            />
          </Flex>
          {meeting_rooms?.length > 0 && (
            <Flex flexDirection="column" gap="2px">
              <Text size="sm-semi-bold">Meeting Room</Text>
              {meeting_rooms?.map((meetingRoom) => (
                <Text key={meetingRoom?.name} fontSize="sm">
                  {meetingRoom?.name}
                </Text>
              ))}
            </Flex>
          )}
          {isReminderNotificationType && (
            <Flex flexDirection="column" gap="2px">
              <Text size="ats-sm-bold">Interview kit</Text>
              <Link
                fontSize="12px"
                display="flex"
                alignItems="center"
                gap="3px"
                href={interview_url}
                target="_blank"
              >
                <Text size="ats-sm">{interview_name}</Text>
                <LinkExternal01 size={12} className="kula-icon" />
              </Link>
            </Flex>
          )}
          {requested_by?.name && (
            <>
              <Flex flexDirection="column" gap="2px">
                <Text size="sm-semi-bold">Scheduled by</Text>
                <Flex gap="3px">
                  <Avatar
                    name={requested_by?.name}
                    src={requested_by?.avatar_url}
                    borderRadius="4px"
                    size="2xs"
                  />
                  <Text fontSize="sm">{requested_by?.name}</Text>
                </Flex>
              </Flex>
            </>
          )}
        </Flex>
        <NotificationTime notification={notification} />
        {showButton && (event_url || interview_url) && (
          <Button
            w="fit-content"
            variant="ats-primary"
            borderRadius="8px"
            onClick={() => {
              markAsRead();
              window.open(
                isReminderNotificationType ? event_url : interview_url,
                '_target'
              );
              onClose();
            }}
            maxH="34px"
            data-test-id="view-calendar-button"
          >
            {isReminderNotificationType
              ? 'View on your calendar'
              : 'View interview'}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
